import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Popper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AppsIcon from '@mui/icons-material/Apps';
import { TableColumnSettings } from 'app/types';
import {
  ResolvedTableSettings,
  TABLE_COLUMN_LABELS,
  TABLE_PAGE_SIZE_OPTIONS,
  TableColumnKey,
} from 'components/tableSettings';
import { Checkbox, Switch } from './primitives';
import {
  VOCAB_COLUMN_LABELS_UA,
  VOCAB_STATUSES,
  statusColor,
} from './meta';
import { UseVocabularyWordsViewState } from './useVocabularyWordsView';

const labelFor = (key: string): string =>
  VOCAB_COLUMN_LABELS_UA[key] ??
  TABLE_COLUMN_LABELS[key as TableColumnKey] ??
  key;

interface SettingsPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Existing column-settings system (BE-persisted via `useTableSettings`). */
  tableSettings: ResolvedTableSettings;
  onSaveTableSettings: (next: ResolvedTableSettings) => Promise<void> | void;
  onResetTableSettings: () => Promise<void> | void;
  /** Local panel settings (cards-per-row, example, highlight). */
  view: UseVocabularyWordsViewState;
  /** Which columns are togglable for the cards view (label list). */
  cardFieldKeys: TableColumnKey[];
}

const LOCKED_KEYS: TableColumnKey[] = ['select', 'number', 'word'];

const GroupLabel: React.FC<{
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ children, right }) => (
  <Stack
    direction={'row'}
    alignItems={'center'}
    justifyContent={'space-between'}
    sx={{ mb: 0.875, px: 0.25 }}
  >
    <Typography
      sx={{
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
    {right}
  </Stack>
);

const ColumnRow: React.FC<{
  col: TableColumnSettings;
  index: number;
  total: number;
  onToggle: () => void;
  onMove: (dir: -1 | 1) => void;
}> = ({ col, index, total, onToggle, onMove }) => {
  const locked = LOCKED_KEYS.includes(col.key as TableColumnKey);
  const label = labelFor(col.key);
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      spacing={1}
      sx={{
        borderRadius: 1.25,
        height: 40,
        pl: 0.5,
        pr: 0.75,
        '&:hover': { bgcolor: 'background.default' },
      }}
    >
      <DragIndicatorIcon
        sx={{ color: 'text.secondary', cursor: 'grab', fontSize: 19, opacity: 0.5 }}
      />
      {locked ? (
        <Box
          title={'Завжди видима'}
          sx={{
            alignItems: 'center',
            color: 'text.secondary',
            display: 'inline-flex',
            flex: 'none',
            height: 20,
            justifyContent: 'center',
            opacity: 0.6,
            width: 20,
          }}
        >
          <LockIcon sx={{ fontSize: 16 }} />
        </Box>
      ) : (
        <Checkbox checked={col.visible} onClick={onToggle} />
      )}
      <Typography
        sx={{
          color: col.visible || locked ? 'text.primary' : 'text.secondary',
          flex: 1,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Box
        component={'button'}
        onClick={() => onMove(-1)}
        disabled={index === 0}
        sx={{
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 1,
          color: 'text.secondary',
          cursor: index === 0 ? 'default' : 'pointer',
          display: 'inline-flex',
          height: 26,
          justifyContent: 'center',
          opacity: index === 0 ? 0.25 : 1,
          width: 26,
          '&:hover': { bgcolor: 'background.default' },
        }}
      >
        <KeyboardArrowUpIcon sx={{ fontSize: 19 }} />
      </Box>
      <Box
        component={'button'}
        onClick={() => onMove(1)}
        disabled={index === total - 1}
        sx={{
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 1,
          color: 'text.secondary',
          cursor: index === total - 1 ? 'default' : 'pointer',
          display: 'inline-flex',
          height: 26,
          justifyContent: 'center',
          opacity: index === total - 1 ? 0.25 : 1,
          width: 26,
          '&:hover': { bgcolor: 'background.default' },
        }}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: 19 }} />
      </Box>
    </Stack>
  );
};

const SettingsPopover: React.FC<SettingsPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  tableSettings,
  onSaveTableSettings,
  onResetTableSettings,
  view,
  cardFieldKeys,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [draft, setDraft] = useState<ResolvedTableSettings>(tableSettings);

  useEffect(() => {
    if (open) setDraft(tableSettings);
  }, [open, tableSettings]);

  const inTable = view.viewMode === 'table';
  const cols = draft.columns;
  const visCount = cols.filter((c) => c.visible).length;
  const allVisible = visCount === cols.length;

  const toggleColumn = (key: string) => {
    const next = cols.map((c) =>
      c.key === key && !LOCKED_KEYS.includes(c.key as TableColumnKey)
        ? { ...c, visible: !c.visible }
        : c,
    );
    const updated = { ...draft, columns: next };
    setDraft(updated);
    void onSaveTableSettings(updated);
  };

  const moveColumn = (idx: number, dir: -1 | 1) => {
    const next = [...cols];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    const reordered = next.map((c, i) => ({ ...c, order: i }));
    const updated = { ...draft, columns: reordered };
    setDraft(updated);
    void onSaveTableSettings(updated);
  };

  const showAll = () => {
    const next = cols.map((c) => ({ ...c, visible: true }));
    const updated = { ...draft, columns: next };
    setDraft(updated);
    void onSaveTableSettings(updated);
  };

  const setPageSize = (n: number) => {
    const updated = { ...draft, pageSize: n };
    setDraft(updated);
    void onSaveTableSettings(updated);
  };

  const setHighlight = (
    field: 'highlightLearned' | 'highlightActiveSpeech',
    val: boolean,
  ) => {
    const updated = { ...draft, [field]: val };
    setDraft(updated);
    void onSaveTableSettings(updated);
  };

  const cardKeyVisible = (key: string) => {
    const c = cols.find((x) => x.key === key);
    return c ? c.visible : true;
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={'bottom-end'}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'primary.tableBorder',
            borderRadius: 2,
            boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 560,
            overflow: 'hidden',
            width: { xs: 320, sm: 380 },
          }}
        >
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.25}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'primary.tableBorder',
              p: '14px 16px',
            }}
          >
            <TuneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
              {inTable ? 'Налаштування таблиці' : 'Налаштування карток'}
            </Typography>
            <Box
              component={'button'}
              onClick={onClose}
              sx={{
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                color: 'text.secondary',
                cursor: 'pointer',
                display: 'inline-flex',
                height: 30,
                justifyContent: 'center',
                width: 30,
                '&:hover': { bgcolor: 'background.default' },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </Box>
          </Stack>

          <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 16px' }}>
            {inTable ? (
              <>
                <GroupLabel
                  right={
                    <Box
                      component={'button'}
                      onClick={showAll}
                      disabled={allVisible}
                      sx={{
                        background: 'transparent',
                        border: 'none',
                        color: allVisible ? 'text.secondary' : 'primary.main',
                        cursor: allVisible ? 'default' : 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12.5,
                        fontWeight: 700,
                        opacity: allVisible ? 0.5 : 1,
                        p: 0,
                      }}
                    >
                      {'Показати всі'}
                    </Box>
                  }
                >
                  {`Колонки · ${visCount} з ${cols.length}`}
                </GroupLabel>
                <Box sx={{ mb: 2.25 }}>
                  {cols.map((col, i) => (
                    <ColumnRow
                      key={col.key}
                      col={col}
                      index={i}
                      total={cols.length}
                      onToggle={() => toggleColumn(col.key)}
                      onMove={(dir) => moveColumn(i, dir)}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <GroupLabel>{'Карток у рядку'}</GroupLabel>
                <Stack direction={'row'} spacing={0.75} sx={{ mb: 2.25 }}>
                  {[2, 3, 4].map((n) => {
                    const on = view.settings.cardsPerRow === n;
                    const Icon = n === 2 ? GridViewIcon : n === 3 ? ViewModuleIcon : AppsIcon;
                    return (
                      <Box
                        key={n}
                        component={'button'}
                        onClick={() => view.setSettings('cardsPerRow', n)}
                        sx={{
                          alignItems: 'center',
                          bgcolor: on ? 'primary.main' : 'background.default',
                          border: '1px solid',
                          borderColor: on ? 'primary.main' : 'primary.tableBorder',
                          borderRadius: 1.25,
                          color: on ? 'common.white' : 'text.primary',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          flex: 1,
                          fontFamily: 'inherit',
                          fontSize: 14,
                          fontWeight: 700,
                          gap: 0.875,
                          height: 44,
                          justifyContent: 'center',
                          transition: 'all .14s ease',
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                        {n}
                      </Box>
                    );
                  })}
                </Stack>

                <GroupLabel>{'Поля картки'}</GroupLabel>
                <Box sx={{ mb: 2.25 }}>
                  {cardFieldKeys.map((key) => {
                    const label = labelFor(key);
                    const on = cardKeyVisible(key);
                    return (
                      <Stack
                        key={key}
                        direction={'row'}
                        alignItems={'center'}
                        spacing={1.25}
                        sx={{
                          borderRadius: 1.25,
                          cursor: 'pointer',
                          height: 40,
                          px: 0.5,
                          '&:hover': { bgcolor: 'background.default' },
                        }}
                        onClick={() => toggleColumn(key)}
                      >
                        <Checkbox
                          checked={on}
                          onClick={() => toggleColumn(key)}
                        />
                        <Typography
                          sx={{
                            color: on ? 'text.primary' : 'text.secondary',
                            flex: 1,
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {label}
                        </Typography>
                      </Stack>
                    );
                  })}
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={1.25}
                    sx={{
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      height: 40,
                      px: 0.5,
                      '&:hover': { bgcolor: 'background.default' },
                    }}
                    onClick={() => view.setSettings('showExample', !view.settings.showExample)}
                  >
                    <Checkbox
                      checked={view.settings.showExample}
                      onClick={() => view.setSettings('showExample', !view.settings.showExample)}
                    />
                    <Typography
                      sx={{
                        color: view.settings.showExample
                          ? 'text.primary'
                          : 'text.secondary',
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {'Приклад'}
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}

            <GroupLabel>{'Слів на сторінці'}</GroupLabel>
            <Stack direction={'row'} spacing={0.75} sx={{ mb: 2.25 }}>
              {TABLE_PAGE_SIZE_OPTIONS.map((n) => {
                const on = draft.pageSize === n;
                return (
                  <Box
                    key={n}
                    component={'button'}
                    onClick={() => setPageSize(n)}
                    sx={{
                      bgcolor: on ? 'primary.main' : 'background.default',
                      border: '1px solid',
                      borderColor: on ? 'primary.main' : 'primary.tableBorder',
                      borderRadius: 1.25,
                      color: on ? 'common.white' : 'text.primary',
                      cursor: 'pointer',
                      flex: 1,
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 700,
                      height: 38,
                      transition: 'all .14s ease',
                    }}
                  >
                    {n}
                  </Box>
                );
              })}
            </Stack>

            <GroupLabel>{'Підсвітка рядків'}</GroupLabel>
            <Stack spacing={0.25}>
              {(
                [
                  ['highlightActiveSpeech', VOCAB_STATUSES.ACTIVE, 'ACTIVE'] as const,
                  ['highlightLearned', VOCAB_STATUSES.LEARNED, 'LEARNED'] as const,
                ]
              ).map(([fieldKey, meta, sk]) => {
                const c = statusColor(sk, mode);
                const on = draft[fieldKey as keyof ResolvedTableSettings] as boolean;
                return (
                  <Stack
                    key={fieldKey}
                    direction={'row'}
                    alignItems={'center'}
                    spacing={1.25}
                    sx={{ height: 42 }}
                  >
                    <Box
                      sx={{
                        bgcolor: c,
                        borderRadius: '50%',
                        flex: 'none',
                        height: 11,
                        width: 11,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        component={'span'}
                        sx={{ fontSize: 14, fontWeight: 600 }}
                      >
                        {meta.label}
                      </Typography>
                      <Typography
                        component={'span'}
                        sx={{ color: 'text.secondary', fontSize: 13, ml: 0.5 }}
                      >
                        {`— ${meta.desc}`}
                      </Typography>
                    </Box>
                    <Switch
                      on={on}
                      color={c}
                      onClick={() =>
                        setHighlight(
                          fieldKey as 'highlightLearned' | 'highlightActiveSpeech',
                          !on,
                        )
                      }
                    />
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={1.5}
            sx={{
              borderTop: '1px solid',
              borderColor: 'primary.tableBorder',
              p: '12px 16px',
            }}
          >
            <Box
              component={'button'}
              onClick={() => {
                void onResetTableSettings();
                view.resetPanelSettings();
              }}
              sx={{
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                color: 'text.secondary',
                cursor: 'pointer',
                display: 'inline-flex',
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: 600,
                gap: 0.625,
                p: 0,
                '&:hover': { color: 'text.primary' },
              }}
            >
              <RestartAltIcon sx={{ fontSize: 18 }} />
              {'Скинути до типових'}
            </Box>
            <Button
              size={'small'}
              variant={'contained'}
              startIcon={<CheckIcon />}
              onClick={onClose}
              sx={{ height: 36, px: 1.5 }}
            >
              {'Готово'}
            </Button>
          </Stack>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default SettingsPopover;
